/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationOnEstimateExpirationType } from '@1po/1po-bff-fe-spec/generated/user/request/UpdateUserNotificationEstimateExpirationRequest';
import {
  EstimateNotificationType,
  getUserNotificationSettings,
  updateUserNotificationEstimateExpirationPeriodRequestSaga,
  updateUserNotificationSettingsRequestSaga,
} from 'domains/user';
import {
  NotificationCheckbox,
  NotificationCheckboxProps,
} from 'pages/SettingsPage/NotificationCenter/NotificationUtils/NotificationCheckbox';
import { NotificationSwitch } from 'pages/SettingsPage/NotificationCenter/NotificationUtils/NotificationSwitch';
import { SDivider, SectionWrapper } from 'pages/SettingsPage/ProfilePage.styled';
import { Flex } from 'UI';

export const EstimateCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const notificationSettings = useSelector(getUserNotificationSettings);

  const handleSwitchEstimateNotificationEnd = (enabled: boolean) => {
    dispatch(updateUserNotificationSettingsRequestSaga({ notificationType: 'ESTIMATE_REACHING_END_OF_LIFE', enabled }));
  };

  const isSelectedPeriodType = (periodType: NotificationOnEstimateExpirationType) =>
    notificationSettings?.estimateExpirationPeriodNotifications?.find((x) => x === periodType) !== undefined;

  const selectPeriod = (enabled: boolean, periodType: NotificationOnEstimateExpirationType) => {
    const prevState = notificationSettings?.estimateExpirationPeriodNotifications ?? [];
    const periods = (enabled
      ? [...prevState, periodType]
      : prevState.filter((x) => x !== periodType)) as NotificationOnEstimateExpirationType[];
    dispatch(updateUserNotificationEstimateExpirationPeriodRequestSaga({ periods }));
  };

  const estimateEnabled =
    notificationSettings?.notificationTypes?.find(
      (notificationType) => notificationType.notificationType === 'ESTIMATE_REACHING_END_OF_LIFE',
    )?.enabled ?? false;

  const estimateCheckboxData: NotificationCheckboxProps[] = [
    {
      key: EstimateNotificationType.PT720H,
      label: t('settings.notification_center.period_30days', '30 days'),
      checked: isSelectedPeriodType(EstimateNotificationType.PT720H),
      onChange: (enabled) => selectPeriod(enabled, EstimateNotificationType.PT720H),
      disabled: !estimateEnabled,
    },
    {
      key: EstimateNotificationType.PT168H,
      label: t('settings.notification_center.period_7days', '7 days'),
      checked: isSelectedPeriodType(EstimateNotificationType.PT168H),
      onChange: (enabled) => selectPeriod(enabled, EstimateNotificationType.PT168H),
      disabled: !estimateEnabled,
    },
    {
      key: EstimateNotificationType.PT48H,
      label: t('settings.notification_center.period_48hours', '48 hours'),
      checked: isSelectedPeriodType(EstimateNotificationType.PT48H),
      onChange: (enabled) => selectPeriod(enabled, EstimateNotificationType.PT48H),
      disabled: !estimateEnabled,
    },
  ];

  return (
    <SectionWrapper>
      <Flex direction={'column'} gap={15}>
        <NotificationSwitch
          title={t('settings.notification_center.estimate', 'Estimate')}
          label={t('settings.notification_center.estimate_notify_receive', 'Receive notifications  ...')}
          isEnabled={estimateEnabled}
          onChange={handleSwitchEstimateNotificationEnd}
          data-cy={'toggle-notification-estimate'}
        />
        <SDivider />
        <NotificationSwitch
          label={t(
            'settings.notification_center.estimate_expiration_notification',
            'When estimate is about to expire (Estimations are saved in your history for a total of 90 days)',
          )}
          isEnabled={estimateEnabled}
          onChange={handleSwitchEstimateNotificationEnd}
          data-cy={'toggle-notification-estimate-expiration'}
        />
        <Flex direction={'column'} gap={10}>
          {estimateCheckboxData.map((data) => (
            // eslint-disable-next-line
            <NotificationCheckbox {...data} />
          ))}
        </Flex>
      </Flex>
    </SectionWrapper>
  );
};
